import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Search, Users, Printer, Download, User, GraduationCap, BookOpen, Tag, Filter, CheckCircle, XCircle, Loader2, AlertCircle } from 'lucide-react';
import { Logo } from '../components/Logo';
import { useFirebaseRoster } from '../hooks/useFirebaseRoster';

function Gjallarhorn() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { SCHOOL_ROSTER, loading: rosterLoading, error: rosterError } = useFirebaseRoster();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPatrons, setSelectedPatrons] = useState([]);
  const [filterType, setFilterType] = useState('all'); // all, student, parent, faculty
  const [filterGrade, setFilterGrade] = useState('all');
  const [filterTeacher, setFilterTeacher] = useState('all');
  const [labelStartPosition, setLabelStartPosition] = useState(1);
  const [generationMode, setGenerationMode] = useState('letters'); // letters, labels, both

  // Get all patrons with their barcode and PIN
  const allPatrons = useMemo(() => {
    const patrons = [];
    
    // Add students
    SCHOOL_ROSTER.students.forEach(student => {
      if (student.barcode && student.pin) {
        patrons.push({
          type: 'student',
          name: student.student,
          barcode: student.barcode,
          pin: student.pin,
          teacher: student.teacher,
          grade: student.grade,
          sortOrder: getGradeSortOrder(student.grade)
        });
      }
    });
    
    // Add parents
    SCHOOL_ROSTER.parents.forEach(parent => {
      if (parent.barcode && parent.pin) {
        patrons.push({
          type: 'parent',
          name: parent.name,
          barcode: parent.barcode,
          pin: parent.pin,
          children: parent.children,
          teacher: parent.teacher,
          grade: parent.grade,
          sortOrder: 100 // Parents sort after students
        });
      }
    });
    
    // Add faculty
    SCHOOL_ROSTER.faculty.forEach(faculty => {
      if (faculty.barcode && faculty.pin) {
        patrons.push({
          type: 'faculty',
          name: faculty.name,
          barcode: faculty.barcode,
          pin: faculty.pin,
          grade: faculty.grade,
          sortOrder: 200 // Faculty sort after parents
        });
      }
    });
    
    return patrons.sort((a, b) => {
      // Sort by grade order, then by name
      if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
      return a.name.localeCompare(b.name);
    });
  }, [SCHOOL_ROSTER]);

  function getGradeSortOrder(grade) {
    const order = { 'PK4': 1, 'K': 2, '1': 3, '2': 4, '3': 5, '4': 6, '5': 7, 'MS': 8 };
    return order[grade] || 50;
  }

  // Get unique teachers and grades for filters
  const teachers = useMemo(() => {
    const teacherSet = new Set();
    allPatrons.forEach(p => {
      if (p.teacher) teacherSet.add(p.teacher);
    });
    return Array.from(teacherSet).sort();
  }, [allPatrons]);

  const grades = ['PK4', 'K', '1', '2', '3', '4', '5', 'MS'];

  // Filter patrons based on search and filters
  const filteredPatrons = useMemo(() => {
    return allPatrons.filter(patron => {
      // Search filter - exact match on first name, last name, or full name
      if (searchTerm) {
        const search = searchTerm.toLowerCase().trim();
        const barcodeMatch = patron.barcode === search;

        // Parse name (format: "Last, First" or just "Name")
        const nameLower = patron.name.toLowerCase();
        const nameParts = nameLower.split(',').map(p => p.trim());
        const lastName = nameParts[0] || '';
        const firstName = nameParts[1] || '';

        // Check exact match on first name, last name, or full name
        const fullNameMatch = nameLower === search;
        const lastNameMatch = lastName === search;
        const firstNameMatch = firstName === search;

        // Also check children names for parents
        let childMatch = false;
        if (patron.children) {
          childMatch = patron.children.some(child => {
            const childLower = child.toLowerCase();
            const childParts = childLower.split(',').map(p => p.trim());
            const childLast = childParts[0] || '';
            const childFirst = childParts[1] || '';
            return childLower === search || childLast === search || childFirst === search;
          });
        }

        if (!fullNameMatch && !lastNameMatch && !firstNameMatch && !barcodeMatch && !childMatch) return false;
      }
      
      // Type filter
      if (filterType !== 'all' && patron.type !== filterType) return false;
      
      // Grade filter
      if (filterGrade !== 'all' && patron.grade !== filterGrade) return false;
      
      // Teacher filter
      if (filterTeacher !== 'all' && patron.teacher !== filterTeacher) return false;
      
      return true;
    });
  }, [allPatrons, searchTerm, filterType, filterGrade, filterTeacher]);

  // Toggle patron selection
  const togglePatronSelection = (patron) => {
    setSelectedPatrons(prev => {
      const isSelected = prev.some(p => p.barcode === patron.barcode);
      if (isSelected) {
        return prev.filter(p => p.barcode !== patron.barcode);
      } else {
        return [...prev, patron];
      }
    });
  };

  // Select all filtered patrons
  const selectAllFiltered = () => {
    setSelectedPatrons(filteredPatrons);
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedPatrons([]);
  };

  // Get label text for a patron - includes delivery info
  const getLabelText = (patron) => {
    if (patron.type === 'student') {
      // 5th grade and under: "Parent of [Name]" with grade/teacher
      // MS: Student name with advisory
      const gradeNum = patron.grade === 'MS' ? 6 : 
                       patron.grade === 'K' ? 0 : 
                       patron.grade === 'PK4' ? -1 :
                       parseInt(patron.grade);
      if (gradeNum <= 5) {
        return `<div>Parent of ${patron.name}</div><div style="font-size: 9px; margin-top: 2px;">${patron.grade} • ${patron.teacher}</div>`;
      } else {
        return `<div>${patron.name}</div><div style="font-size: 9px; margin-top: 2px;">MS • ${patron.teacher}</div>`;
      }
    } else if (patron.type === 'parent') {
      // Parents: their name with c/o oldest child for delivery
      // Find oldest child - the one matching the parent's grade/teacher
      let oldestChild = patron.children?.[0] || 'Student';
      
      // If multiple children, try to find the one in the matching grade
      if (patron.children && patron.children.length > 1) {
        // The parent's grade field represents oldest child's grade
        // For now, use first child listed (usually oldest)
        oldestChild = patron.children[0];
      }
      
      return `<div>${patron.name}</div><div style="font-size: 9px; margin-top: 2px;">c/o ${oldestChild}</div><div style="font-size: 9px;">${patron.grade} • ${patron.teacher}</div>`;
    } else {
      // Faculty: name with "Faculty" designation
      return `<div>${patron.name}</div><div style="font-size: 9px; margin-top: 2px;">Faculty</div>`;
    }
  };

  // Generate letter HTML for a single patron
  const generateLetterHTML = (patron) => {
    const isStudent = patron.type === 'student';
    const isParent = patron.type === 'parent';
    const isFaculty = patron.type === 'faculty';
    
    // Color schemes
    let colorScheme = {};
    if (isStudent) {
      colorScheme = {
        primary: '#0891B2',    // cyan-600
        secondary: '#06B6D4',  // cyan-500
        background: '#CFFAFE', // cyan-100
        border: '#A5F3FC',     // cyan-200
        accent: '#0E7490'      // cyan-700
      };
    } else if (isParent) {
      colorScheme = {
        primary: '#7C3AED',    // violet-600
        secondary: '#8B5CF6',  // violet-500
        background: '#EDE9FE', // violet-100
        border: '#DDD6FE',     // violet-200
        accent: '#6D28D9'      // violet-700
      };
    } else {
      colorScheme = {
        primary: '#059669',    // emerald-600
        secondary: '#10B981',  // emerald-500
        background: '#D1FAE5', // emerald-100
        border: '#A7F3D0',     // emerald-200
        accent: '#047857'      // emerald-700
      };
    }

    // Patron info section - compact
    let patronInfoHTML = '';
    if (isStudent) {
      patronInfoHTML = `
        <div style="background: ${colorScheme.background}; padding: 10px 12px; border-radius: 6px; margin-bottom: 12px; border: 1px solid ${colorScheme.border};">
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 4px; font-size: 12px;">
            <div><strong>Student:</strong> ${patron.name}</div>
            <div><strong>Grade:</strong> ${patron.grade}</div>
            <div><strong>Teacher:</strong> ${patron.teacher}</div>
            <div><strong>Date:</strong> ${new Date().toLocaleDateString()}</div>
          </div>
        </div>
      `;
    } else if (isParent) {
      const childrenList = patron.children ? patron.children.join(', ') : 'N/A';
      patronInfoHTML = `
        <div style="background: ${colorScheme.background}; padding: 10px 12px; border-radius: 6px; margin-bottom: 12px; border: 1px solid ${colorScheme.border};">
          <div style="font-size: 12px;">
            <div><strong>Parent:</strong> ${patron.name} &nbsp;&nbsp; <strong>Children:</strong> ${childrenList}</div>
          </div>
        </div>
      `;
    } else {
      patronInfoHTML = `
        <div style="background: ${colorScheme.background}; padding: 10px 12px; border-radius: 6px; margin-bottom: 12px; border: 1px solid ${colorScheme.border};">
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 4px; font-size: 12px;">
            <div><strong>Name:</strong> ${patron.name}</div>
            <div><strong>Department:</strong> ${patron.grade || 'N/A'}</div>
          </div>
        </div>
      `;
    }

    return `
      <div class="letter-page" style="page-break-after: always; page-break-inside: avoid;">
        <div style="font-family: Calibri, 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; line-height: 1.3; color: #333; padding: 15px 20px; max-width: 7.5in; margin: 0 auto;">
          
          <!-- Header -->
          <div style="text-align: center; border-bottom: 2px solid ${colorScheme.primary}; padding-bottom: 6px; margin-bottom: 10px;">
            <div style="font-size: 18px; font-weight: bold; color: ${colorScheme.primary}; letter-spacing: 0.5px;">Holy Family Catholic School</div>
            <div style="font-size: 12px; color: ${colorScheme.secondary}; font-weight: 600;">HFCS Library</div>
            <div style="font-size: 9px; color: #666;">9400 Neenah Ave • Austin, TX 78717</div>
          </div>
          
          <!-- Title -->
          <div style="font-size: 16px; font-weight: bold; text-align: center; margin: 10px 0; padding: 8px; background: ${colorScheme.background}; color: ${colorScheme.accent}; border: 2px solid ${colorScheme.primary}; border-radius: 6px;">
            📖 Your Library Account Information
          </div>
          
          <!-- Patron Info -->
          ${patronInfoHTML}
          
          <!-- Account Credentials Box -->
          <div style="background: linear-gradient(135deg, ${colorScheme.background} 0%, #fff 100%); border: 2px solid ${colorScheme.primary}; border-radius: 8px; padding: 12px; margin: 10px 0; text-align: center;">
            <div style="font-size: 14px; font-weight: bold; color: ${colorScheme.accent}; margin-bottom: 8px;">🔐 Your Login Credentials</div>
            <div style="display: inline-block; text-align: left; background: white; padding: 10px 20px; border-radius: 6px; border: 1px solid ${colorScheme.border};">
              <div style="font-size: 13px; margin-bottom: 6px;">
                <strong style="color: ${colorScheme.primary};">Patron Number:</strong> 
                <span style="font-family: monospace; font-size: 16px; font-weight: bold; color: #1F2937; background: #F3F4F6; padding: 2px 8px; border-radius: 4px; margin-left: 8px;">${patron.barcode}</span>
              </div>
              <div style="font-size: 13px;">
                <strong style="color: ${colorScheme.primary};">PIN:</strong> 
                <span style="font-family: monospace; font-size: 16px; font-weight: bold; color: #1F2937; background: #F3F4F6; padding: 2px 8px; border-radius: 4px; margin-left: 8px;">${patron.pin}</span>
              </div>
            </div>
            <div style="font-size: 9px; color: #666; margin-top: 6px; font-style: italic;">Keep this information safe - you'll need it to access your library account!</div>
          </div>
          
          <!-- Instructions -->
          <div style="margin: 10px 0;">
            <div style="font-size: 14px; font-weight: bold; color: ${colorScheme.primary}; margin-bottom: 8px; border-bottom: 1px solid ${colorScheme.border}; padding-bottom: 4px;">
              📋 How to Access Your Library Account
            </div>
            
            <!-- Step 1 -->
            <div style="background: #F8FAFC; border: 1px solid #E2E8F0; border-radius: 6px; padding: 8px 10px; margin-bottom: 8px;">
              <div style="font-size: 12px; font-weight: bold; color: ${colorScheme.accent}; margin-bottom: 4px;">
                Step 1: Go to the Library Catalog
              </div>
              <div style="font-size: 11px; color: #4B5563;">
                Open your web browser and go to: <strong style="color: ${colorScheme.primary};">https://opac.libraryworld.com/</strong><br/>
                Enter <strong>"hfcs"</strong> for the Library Name and leave Password blank, then click <strong>Sign In</strong>.
              </div>
              <div style="text-align: center; margin-top: 6px;">
                <img src="/screenshots/opac-login.jpg" alt="OPAC Login" style="max-width: 280px; border: 1px solid #CBD5E1; border-radius: 4px;" />
              </div>
            </div>
            
            <!-- Step 2 -->
            <div style="background: #F8FAFC; border: 1px solid #E2E8F0; border-radius: 6px; padding: 8px 10px; margin-bottom: 8px;">
              <div style="font-size: 12px; font-weight: bold; color: ${colorScheme.accent}; margin-bottom: 4px;">
                Step 2: Explore the Catalog
              </div>
              <div style="font-size: 11px; color: #4B5563;">
                You're now in the HFCS Library catalog! You can search for books, browse by subject, and access online resources like Encyclopedia Britannica and EBSCO databases.
              </div>
              <div style="text-align: center; margin-top: 6px;">
                <img src="/screenshots/opac-home.jpg" alt="OPAC Home" style="max-width: 320px; border: 1px solid #CBD5E1; border-radius: 4px;" />
              </div>
            </div>
            
            <!-- Step 3 -->
            <div style="background: #F8FAFC; border: 1px solid #E2E8F0; border-radius: 6px; padding: 8px 10px; margin-bottom: 8px;">
              <div style="font-size: 12px; font-weight: bold; color: ${colorScheme.accent}; margin-bottom: 4px;">
                Step 3: Sign In to Your Account
              </div>
              <div style="font-size: 11px; color: #4B5563;">
                Click on <strong>"User"</strong> in the top right corner, then click <strong>"My Account"</strong>. Enter your <strong>Patron Number</strong> and <strong>PIN</strong> from above to sign in.
              </div>
              <div style="text-align: center; margin-top: 6px;">
                <img src="/screenshots/opac-account-signin.jpg" alt="Account Sign In" style="max-width: 320px; border: 1px solid #CBD5E1; border-radius: 4px;" />
              </div>
            </div>
          </div>
          
          <!-- What You Can See -->
          <div style="background: ${colorScheme.background}; border: 1px solid ${colorScheme.border}; border-radius: 6px; padding: 10px; margin: 10px 0;">
            <div style="font-size: 12px; font-weight: bold; color: ${colorScheme.accent}; margin-bottom: 8px;">
              ✨ What You Can See in Your Account
            </div>
            <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 6px; font-size: 10px;">
              <div style="background: white; padding: 6px; border-radius: 4px; border: 1px solid ${colorScheme.border};">
                <strong style="color: ${colorScheme.primary};">📚 Checked Out</strong><br/>
                <span style="color: #6B7280;">Books currently checked out</span>
              </div>
              <div style="background: white; padding: 6px; border-radius: 4px; border: 1px solid ${colorScheme.border};">
                <strong style="color: #DC2626;">⏰ Overdue</strong><br/>
                <span style="color: #6B7280;">Books needing return</span>
              </div>
              <div style="background: white; padding: 6px; border-radius: 4px; border: 1px solid ${colorScheme.border};">
                <strong style="color: ${colorScheme.primary};">📋 On Hold</strong><br/>
                <span style="color: #6B7280;">Books waiting for you</span>
              </div>
              <div style="background: white; padding: 6px; border-radius: 4px; border: 1px solid ${colorScheme.border};">
                <strong style="color: ${colorScheme.primary};">📖 On Reserve</strong><br/>
                <span style="color: #6B7280;">Class reserves</span>
              </div>
              <div style="background: white; padding: 6px; border-radius: 4px; border: 1px solid ${colorScheme.border};">
                <strong style="color: ${colorScheme.primary};">📜 History</strong><br/>
                <span style="color: #6B7280;">Borrowing history</span>
              </div>
              <div style="background: white; padding: 6px; border-radius: 4px; border: 1px solid ${colorScheme.border};">
                <strong style="color: ${colorScheme.primary};">💳 Account</strong><br/>
                <span style="color: #6B7280;">Account status</span>
              </div>
            </div>
          </div>
          
          <!-- Footer - matching OdinsRavens style -->
          <div style="margin-top: 10px; padding-top: 8px; border-top: 1px solid #E5E7EB; font-size: 9px; line-height: 1.3;">
            <div style="padding: 5px; background: #F3F4F6; border-radius: 4px;">
              <strong>Questions?</strong> Contact Dr. Kahn at <a href="mailto:vkahn@holyfamilycs.org" style="color: ${colorScheme.primary}; text-decoration: none;">vkahn@holyfamilycs.org</a>
            </div>
          </div>
          
        </div>
      </div>
    `;
  };

  // Generate all letters HTML
  const generateAllLettersHTML = (patrons) => {
    const lettersHTML = patrons.map(p => generateLetterHTML(p)).join('\n');
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Library Account Letters - ${new Date().toLocaleDateString()}</title>
        <style>
          @page {
            size: letter;
            margin: 0.5in;
          }
          @media print {
            body { margin: 0; padding: 0; }
            .letter-page { page-break-after: always; page-break-inside: avoid; }
            .letter-page:last-child { page-break-after: avoid; }
            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
          }
          body {
            font-family: Calibri, 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            margin: 0;
            padding: 0;
          }
          img {
            max-width: 100%;
            height: auto;
          }
        </style>
      </head>
      <body>
        ${lettersHTML}
      </body>
      </html>
    `;
  };

  // Generate Avery 5160 labels HTML
  const generateLabelsHTML = (patrons, startPosition = 1) => {
    const labelsPerRow = 3;
    const labelsPerSheet = 30;
    const labelWidth = 2.625; // inches
    const labelHeight = 1; // inches
    const marginLeft = 0.1875; // inches
    const marginTop = 0.5; // inches
    const gapH = 0.125; // inches between columns
    
    // Calculate positions
    const getLabelPosition = (index) => {
      const adjustedIndex = index + startPosition - 1;
      const row = Math.floor(adjustedIndex / labelsPerRow);
      const col = adjustedIndex % labelsPerRow;
      
      return {
        left: marginLeft + (col * (labelWidth + gapH)),
        top: marginTop + (row * labelHeight),
        sheetIndex: Math.floor(adjustedIndex / labelsPerSheet)
      };
    };

    // Group labels by sheet
    const sheets = [];
    patrons.forEach((patron, index) => {
      const pos = getLabelPosition(index);
      if (!sheets[pos.sheetIndex]) {
        sheets[pos.sheetIndex] = [];
      }
      sheets[pos.sheetIndex].push({
        patron,
        left: pos.left % (8.5), // Reset for new sheet
        top: pos.top % (11), // Reset for new sheet
        localIndex: (index + startPosition - 1) % labelsPerSheet
      });
    });

    const sheetsHTML = sheets.map((sheetLabels, sheetIndex) => {
      const labelsHTML = sheetLabels.map(({ patron, localIndex }) => {
        const row = Math.floor(localIndex / labelsPerRow);
        const col = localIndex % labelsPerRow;
        const left = marginLeft + (col * (labelWidth + gapH));
        const top = marginTop + (row * labelHeight);
        
        const labelText = getLabelText(patron);
        
        return `
          <div style="
            position: absolute;
            left: ${left}in;
            top: ${top}in;
            width: ${labelWidth}in;
            height: ${labelHeight}in;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            text-align: center;
            font-family: Arial, sans-serif;
            font-size: 11px;
            font-weight: 500;
            color: #1F2937;
            overflow: hidden;
            box-sizing: border-box;
            padding: 2px 4px;
            line-height: 1.2;
          ">
            ${labelText}
          </div>
        `;
      }).join('');

      return `
        <div class="label-sheet" style="
          position: relative;
          width: 8.5in;
          height: 11in;
          page-break-after: always;
          background: white;
        ">
          ${labelsHTML}
        </div>
      `;
    }).join('');

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Patron Labels - Avery 5160 - ${new Date().toLocaleDateString()}</title>
        <style>
          @page {
            size: letter;
            margin: 0;
          }
          @media print {
            body { margin: 0; padding: 0; }
            .label-sheet { page-break-after: always; }
            .label-sheet:last-child { page-break-after: avoid; }
          }
          body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 0;
          }
        </style>
      </head>
      <body>
        ${sheetsHTML}
      </body>
      </html>
    `;
  };

  // Print letters
  const printLetters = () => {
    if (selectedPatrons.length === 0) {
      alert('Please select at least one patron');
      return;
    }
    
    const html = generateAllLettersHTML(selectedPatrons);
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
      setTimeout(() => printWindow.print(), 500);
    }
  };

  // Download letters
  const downloadLetters = () => {
    if (selectedPatrons.length === 0) {
      alert('Please select at least one patron');
      return;
    }
    
    const html = generateAllLettersHTML(selectedPatrons);
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Library_Account_Letters_${new Date().toISOString().split('T')[0]}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Print labels
  const printLabels = () => {
    if (selectedPatrons.length === 0) {
      alert('Please select at least one patron');
      return;
    }
    
    const html = generateLabelsHTML(selectedPatrons, labelStartPosition);
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
      setTimeout(() => printWindow.print(), 500);
    }
  };

  // Download labels
  const downloadLabels = () => {
    if (selectedPatrons.length === 0) {
      alert('Please select at least one patron');
      return;
    }
    
    const html = generateLabelsHTML(selectedPatrons, labelStartPosition);
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Patron_Labels_${new Date().toISOString().split('T')[0]}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Calculate number of label sheets needed
  const calculateLabelSheets = () => {
    const labelsPerSheet = 30;
    const firstSheetCapacity = labelsPerSheet - labelStartPosition + 1;
    
    if (selectedPatrons.length <= firstSheetCapacity) {
      return 1;
    }
    
    const remainingLabels = selectedPatrons.length - firstSheetCapacity;
    return 1 + Math.ceil(remainingLabels / labelsPerSheet);
  };

  const numLabelSheets = calculateLabelSheets();

  // Stats
  const stats = {
    total: allPatrons.length,
    students: allPatrons.filter(p => p.type === 'student').length,
    parents: allPatrons.filter(p => p.type === 'parent').length,
    faculty: allPatrons.filter(p => p.type === 'faculty').length
  };

  // Show loading state while roster loads
  if (rosterLoading) {
    return (
      <div className="min-h-screen bg-slate-100 flex flex-col items-center justify-center">
        <Loader2 size={48} className="text-cyan-600 animate-spin mb-4" />
        <p className="text-gray-600">Loading roster data...</p>
      </div>
    );
  }

  // Show error state if roster failed to load
  if (rosterError) {
    return (
      <div className="min-h-screen bg-slate-100 flex flex-col items-center justify-center">
        <AlertCircle size={48} className="text-red-500 mb-4" />
        <p className="text-red-600 mb-4">{rosterError}</p>
        <button
          onClick={() => window.location.reload()}
          className="bg-cyan-600 text-white px-4 py-2 rounded-lg hover:bg-cyan-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-gradient-to-r from-cyan-800 to-teal-800 text-white shadow-xl border-b border-cyan-700/50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <Logo size={40} showText={false} />
              <div>
                <h1 className="text-lg sm:text-2xl font-bold tracking-wide">📯 Gjallarhorn</h1>
                <p className="text-cyan-200 text-xs sm:text-sm">Library Account Letters & Labels</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/asgard')}
                className="bg-cyan-700 hover:bg-cyan-600 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg transition-colors text-sm sm:text-base"
              >
                ← Back to Asgard
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          
          {/* Left Column - Selection */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Stats Overview */}
            <div className="bg-white rounded-xl shadow-lg p-6 border border-slate-200">
              <h2 className="text-lg font-bold text-slate-800 mb-4">📊 Patron Overview</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="bg-cyan-50 rounded-lg p-4 text-center border border-cyan-200">
                  <div className="text-2xl font-bold text-cyan-700">{stats.total}</div>
                  <div className="text-sm text-cyan-600">Total Patrons</div>
                </div>
                <div className="bg-blue-50 rounded-lg p-4 text-center border border-blue-200">
                  <div className="text-2xl font-bold text-blue-700">{stats.students}</div>
                  <div className="text-sm text-blue-600">Students</div>
                </div>
                <div className="bg-violet-50 rounded-lg p-4 text-center border border-violet-200">
                  <div className="text-2xl font-bold text-violet-700">{stats.parents}</div>
                  <div className="text-sm text-violet-600">Parents</div>
                </div>
                <div className="bg-emerald-50 rounded-lg p-4 text-center border border-emerald-200">
                  <div className="text-2xl font-bold text-emerald-700">{stats.faculty}</div>
                  <div className="text-sm text-emerald-600">Faculty</div>
                </div>
              </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-xl shadow-lg p-6 border border-slate-200">
              <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center">
                <Filter className="mr-2" size={20} />
                Search & Filter Patrons
              </h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                {/* Search */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Search</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
                    <input
                      type="text"
                      placeholder="Search by name or barcode..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                    />
                  </div>
                </div>
                
                {/* Type Filter */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Type</label>
                  <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                  >
                    <option value="all">All Types</option>
                    <option value="student">Students</option>
                    <option value="parent">Parents</option>
                    <option value="faculty">Faculty</option>
                  </select>
                </div>
                
                {/* Grade Filter */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Grade</label>
                  <select
                    value={filterGrade}
                    onChange={(e) => setFilterGrade(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                  >
                    <option value="all">All Grades</option>
                    {grades.map(g => (
                      <option key={g} value={g}>{g}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              {/* Teacher Filter */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-1">Teacher</label>
                <select
                  value={filterTeacher}
                  onChange={(e) => setFilterTeacher(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                >
                  <option value="all">All Teachers</option>
                  {teachers.map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              
              {/* Selection Buttons */}
              <div className="flex space-x-3">
                <button
                  onClick={selectAllFiltered}
                  className="bg-cyan-600 text-white px-4 py-2 rounded-lg hover:bg-cyan-700 transition flex items-center"
                >
                  <CheckCircle className="mr-2" size={18} />
                  Select All Filtered ({filteredPatrons.length})
                </button>
                <button
                  onClick={clearSelection}
                  className="bg-slate-500 text-white px-4 py-2 rounded-lg hover:bg-slate-600 transition flex items-center"
                >
                  <XCircle className="mr-2" size={18} />
                  Clear Selection
                </button>
              </div>
            </div>

            {/* Patron List */}
            <div className="bg-white rounded-xl shadow-lg border border-slate-200">
              <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-slate-100 to-slate-50 rounded-t-xl">
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-bold text-slate-800">
                    Patrons ({filteredPatrons.length} shown, {selectedPatrons.length} selected)
                  </h2>
                </div>
              </div>
              <div className="max-h-96 overflow-y-auto">
                {filteredPatrons.length === 0 ? (
                  <div className="px-6 py-8 text-center text-slate-500">
                    No patrons match your filters
                  </div>
                ) : (
                  filteredPatrons.map((patron) => {
                    const isSelected = selectedPatrons.some(p => p.barcode === patron.barcode);
                    return (
                      <div
                        key={patron.barcode}
                        onClick={() => togglePatronSelection(patron)}
                        className={`px-6 py-3 border-b border-slate-100 cursor-pointer transition ${
                          isSelected ? 'bg-cyan-50 border-l-4 border-l-cyan-500' : 'hover:bg-slate-50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => {}}
                              className="h-4 w-4 text-cyan-600 rounded"
                            />
                            <div>
                              <div className="font-medium text-slate-800">{patron.name}</div>
                              <div className="text-sm text-slate-500">
                                {patron.type === 'student' && (
                                  <span>Grade {patron.grade} • {patron.teacher}</span>
                                )}
                                {patron.type === 'parent' && (
                                  <span>Parent • {patron.children?.join(', ')}</span>
                                )}
                                {patron.type === 'faculty' && (
                                  <span>Faculty • {patron.grade}</span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className={`text-xs px-2 py-1 rounded-full font-medium ${
                              patron.type === 'student' ? 'bg-blue-100 text-blue-700' :
                              patron.type === 'parent' ? 'bg-violet-100 text-violet-700' :
                              'bg-emerald-100 text-emerald-700'
                            }`}>
                              {patron.type}
                            </div>
                            <div className="text-xs text-slate-500 mt-1 font-mono">
                              {patron.barcode}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Actions */}
          <div className="space-y-6">
            
            {/* Selection Summary */}
            <div className="bg-gradient-to-br from-cyan-600 to-teal-600 rounded-xl shadow-lg p-6 text-white">
              <h2 className="text-lg font-bold mb-4">📋 Selected Patrons</h2>
              <div className="text-4xl font-bold mb-2">{selectedPatrons.length}</div>
              <div className="text-cyan-100 text-sm mb-4">
                {selectedPatrons.filter(p => p.type === 'student').length} students, 
                {' '}{selectedPatrons.filter(p => p.type === 'parent').length} parents, 
                {' '}{selectedPatrons.filter(p => p.type === 'faculty').length} faculty
              </div>
              {selectedPatrons.length > 0 && (
                <div className="text-xs text-cyan-200">
                  Ready to generate {selectedPatrons.length} letter{selectedPatrons.length !== 1 ? 's' : ''} and {numLabelSheets} label sheet{numLabelSheets !== 1 ? 's' : ''}
                </div>
              )}
            </div>

            {/* Generate Letters */}
            <div className="bg-white rounded-xl shadow-lg p-6 border border-slate-200">
              <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center">
                <BookOpen className="mr-2 text-cyan-600" size={20} />
                Generate Letters
              </h2>
              <p className="text-sm text-slate-600 mb-4">
                Create personalized library account letters with login credentials and step-by-step instructions.
              </p>
              <div className="space-y-3">
                <button
                  onClick={printLetters}
                  disabled={selectedPatrons.length === 0}
                  className="w-full bg-cyan-600 text-white py-3 px-4 rounded-lg hover:bg-cyan-700 transition font-medium flex items-center justify-center disabled:bg-slate-300 disabled:cursor-not-allowed"
                >
                  <Printer className="mr-2" size={20} />
                  Print Letters ({selectedPatrons.length})
                </button>
                <button
                  onClick={downloadLetters}
                  disabled={selectedPatrons.length === 0}
                  className="w-full bg-teal-600 text-white py-2 px-4 rounded-lg hover:bg-teal-700 transition flex items-center justify-center disabled:bg-slate-300 disabled:cursor-not-allowed"
                >
                  <Download className="mr-2" size={18} />
                  Download Letters
                </button>
              </div>
            </div>

            {/* Generate Labels */}
            <div className="bg-white rounded-xl shadow-lg p-6 border border-slate-200">
              <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center">
                <Tag className="mr-2 text-violet-600" size={20} />
                Generate Labels (Avery 5160)
              </h2>
              <p className="text-sm text-slate-600 mb-4">
                Print name labels to attach to letters. Labels print in the same order as selected patrons.
              </p>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Start at position:
                </label>
                <select
                  value={labelStartPosition}
                  onChange={(e) => setLabelStartPosition(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500"
                >
                  {Array.from({ length: 30 }, (_, i) => (
                    <option key={i + 1} value={i + 1}>Position {i + 1}</option>
                  ))}
                </select>
                <p className="text-xs text-slate-500 mt-1">
                  Use partially used label sheets by starting at a different position
                </p>
              </div>
              
              {selectedPatrons.length > 0 && (
                <div className="bg-violet-50 border border-violet-200 rounded-lg p-3 mb-4">
                  <div className="text-sm text-violet-800">
                    <strong>{selectedPatrons.length}</strong> label{selectedPatrons.length !== 1 ? 's' : ''} will print on <strong>{numLabelSheets}</strong> sheet{numLabelSheets !== 1 ? 's' : ''}
                  </div>
                </div>
              )}
              
              <div className="space-y-3">
                <button
                  onClick={printLabels}
                  disabled={selectedPatrons.length === 0}
                  className="w-full bg-violet-600 text-white py-3 px-4 rounded-lg hover:bg-violet-700 transition font-medium flex items-center justify-center disabled:bg-slate-300 disabled:cursor-not-allowed"
                >
                  <Printer className="mr-2" size={20} />
                  Print Labels ({selectedPatrons.length})
                </button>
                <button
                  onClick={downloadLabels}
                  disabled={selectedPatrons.length === 0}
                  className="w-full bg-purple-600 text-white py-2 px-4 rounded-lg hover:bg-purple-700 transition flex items-center justify-center disabled:bg-slate-300 disabled:cursor-not-allowed"
                >
                  <Download className="mr-2" size={18} />
                  Download Labels
                </button>
              </div>
            </div>

            {/* Label Preview Info */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <h3 className="font-bold text-amber-800 mb-2">📝 Label Naming Rules</h3>
              <ul className="text-sm text-amber-700 space-y-1">
                <li>• <strong>PK4 - Grade 5:</strong> "Parent of [Name]"</li>
                <li>• <strong>Middle School:</strong> Student's name only</li>
                <li>• <strong>Parents & Faculty:</strong> Their own name</li>
              </ul>
            </div>

            {/* Info about what account shows */}
            <div className="bg-cyan-50 border border-cyan-200 rounded-xl p-4">
              <h3 className="font-bold text-cyan-800 mb-2">📚 Account Features</h3>
              <ul className="text-sm text-cyan-700 space-y-1">
                <li>• Checked Out Items</li>
                <li>• Overdue Items</li>
                <li>• Items on Hold</li>
                <li>• Items on Reserve</li>
                <li>• Circulation History</li>
              </ul>
            </div>

          </div>
        </div>
      </main>
    </div>
  );
}

export default Gjallarhorn;